import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, BarChart3, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { LanguageSelector } from '../common/LanguageSelector';
import { DropdownPortal } from '../common/DropdownPortal';
import { useCompanySettings } from '../../context/CompanySettingsContext';

// Same-origin fallback — served from /public. Never a cross-origin URL,
// so the navbar is immune to CORS errors and third-party image-host outages.
const DEFAULT_LOGO_SRC = '/logo.png';
const DEFAULT_COMPANY_NAME = 'MoveAbroadIQ';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [compareDropdownOpen, setCompareDropdownOpen] = useState(false);
  const location = useLocation();
  const compareTriggerRef = useRef(null);
  const [logoError, setLogoError] = useState(false);

  // Global company settings — SINGLE SOURCE OF TRUTH for logo + name.
  // `logoUrl` is already cache-busted AND same-origin-guarded in the context
  // (external URLs are coerced to /logo.png before they reach us).
  const { logoUrl, companyName, isLoading, hasCustomLogo } = useCompanySettings();

  // Reset logoError if the URL changes (e.g. admin just uploaded a new logo).
  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  // Verification log requested by ops — surfaces exactly which URL the
  // navbar is rendering (after cache-bust + same-origin guard). Production
  // consoles will show e.g. `LOGO: /logo.png?v=1776623953935` and never a
  // cross-origin URL.
  useEffect(() => {
    if (!isLoading) {
      // eslint-disable-next-line no-console
      console.log('LOGO:', logoUrl || '(empty → using fallback /logo.png)');
    }
  }, [isLoading, logoUrl]);

  // Decide what to render:
  //   - Still fetching settings -> skeleton placeholder (no fallback logo flicker)
  //   - settings loaded, logo_url non-empty, not errored -> that URL (same-origin)
  //   - settings loaded, onError fired -> local /logo.png
  const hasSettings = !isLoading;
  const effectiveLogo = !logoError && logoUrl ? logoUrl : DEFAULT_LOGO_SRC;
  const effectiveName = companyName || DEFAULT_COMPANY_NAME;
  // Show the brand text only when rendering the generic default logo (no
  // admin-uploaded brand mark). Custom logos are assumed to contain the
  // brand lockup already — rendering the text next to them duplicates it.
  const showBrandText = !hasCustomLogo || logoError;

  const navLinks = [
    { name: 'Countries', href: '/countries' },
    { name: 'Cities', href: '/cities' },
    { name: 'Tools', href: '/tools' },
    { name: 'Blog', href: '/blog' },
    { name: 'Guides', href: '/guides' },
  ];

  const compareLinks = [
    { name: 'Compare Countries', href: '/tools/compare', icon: Globe },
    { name: 'Compare Cities', href: '/tools/city-comparison', icon: Building2 },
  ];

  const isActive = (path) => location.pathname === path;

  // Close compare dropdown when route changes
  useEffect(() => {
    setCompareDropdownOpen(false);
  }, [location.pathname]);

  const closeCompareDropdown = () => setCompareDropdownOpen(false);

  return (
    <nav className="sticky top-0 z-[9999] bg-white/95 backdrop-blur-sm border-b border-slate-200" style={{ isolation: 'isolate' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo + Home link — reads from global CompanySettings state.
              While settings are loading, show a skeleton placeholder so no
              stale/default logo ever flashes. Link stays clickable throughout.
              Display rule (no duplicate brand, no cross-origin images):
                - admin custom logo present → render ONLY the logo image
                - no custom logo (fallback)  → render /logo.png + company name
              `logoUrl` is same-origin-guarded in CompanySettingsContext, so
              it is always safe to render (no CORS failures possible). */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            {hasSettings ? (
              <img
                key={effectiveLogo}
                src={effectiveLogo}
                alt={effectiveName}
                width={36}
                height={36}
                decoding="async"
                fetchPriority="high"
                className="h-[30px] md:h-9 w-auto object-contain"
                onError={() => {
                  // Only flip to logoError=true if we are NOT already showing
                  // the same-origin default. This prevents an infinite fallback
                  // loop when /logo.png itself fails (very rare, e.g. public
                  // asset mis-deployed), and also prevents a transient single
                  // onError from masking an otherwise-valid custom logo that
                  // eventually loads. React reconciles the src prop in-place —
                  // no DOM manipulation needed.
                  if (effectiveLogo !== DEFAULT_LOGO_SRC) {
                    setLogoError(true);
                  }
                }}
                data-testid="navbar-logo-img"
              />
            ) : (
              <span
                className="h-[30px] md:h-9 w-9 rounded-md bg-slate-100 animate-pulse"
                aria-hidden="true"
                data-testid="navbar-logo-skeleton"
              />
            )}
            {hasSettings && showBrandText && (
              <span
                className="font-bold text-xl text-slate-900 tracking-tight"
                data-testid="navbar-brand-name"
              >
                {effectiveName}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link text-sm ${isActive(link.href) ? 'text-slate-900 font-semibold' : ''}`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons + Language Selector */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageSelector />
            
            {/* Compare Dropdown */}
            <div className="relative">
              <Button 
                ref={compareTriggerRef}
                variant="ghost" 
                className="text-slate-600 hover:text-slate-900 flex items-center gap-1"
                onClick={() => setCompareDropdownOpen(!compareDropdownOpen)}
                aria-expanded={compareDropdownOpen}
                aria-haspopup="menu"
                data-testid="nav-compare-btn"
              >
                <BarChart3 className="w-4 h-4" />
                Compare
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${compareDropdownOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              <DropdownPortal 
                isOpen={compareDropdownOpen} 
                onClose={closeCompareDropdown}
                triggerRef={compareTriggerRef}
                align="right"
              >
                <div 
                  className="dropdown-menu w-48"
                  role="menu"
                  data-testid="compare-dropdown-menu"
                >
                  {compareLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="dropdown-menu-item"
                      onClick={closeCompareDropdown}
                      role="menuitem"
                      data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      <link.icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      <span>{link.name}</span>
                    </Link>
                  ))}
                </div>
              </DropdownPortal>
            </div>
            
            <Link to="/tools/calculator">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg" data-testid="nav-calculate-btn">
                Calculate Cost
              </Button>
            </Link>
          </div>

          {/* Mobile: Language + Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageSelector />
            <button
              className="p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 animate-fadeIn bg-white" style={{ position: 'relative', zIndex: 10000 }}>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg text-sm ${
                    isActive(link.href) ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-600'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="mt-4 px-4 flex flex-col gap-2">
                <Link to="/tools/compare" onClick={() => setIsOpen(false)}>
                  <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                    <Globe className="w-4 h-4" />
                    Compare Countries
                  </Button>
                </Link>
                <Link to="/tools/city-comparison" onClick={() => setIsOpen(false)}>
                  <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Compare Cities
                  </Button>
                </Link>
                <Link to="/tools/calculator" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-slate-900 text-white">Calculate Cost</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
